import { useEffect, useMemo, useRef, useState } from 'react';
import { Modale } from '../ui/Modale';
import {
  CLASSE_CHAMP,
  CLASSE_CHAMP_FAUTIF,
  Case,
  Champ,
  CodePostalEtCommune,
  Section,
} from '../ui/ChampsDossier';
import { detailsRefus, libelleChamp } from '../../lib/erreurs-api';
import { aujourdhui, minoriteALEntree } from '../../lib/dates-civiles';
import {
  Icone,
  Progression,
  TRACE_ALERTE,
  TRACE_COCHE,
  TRACE_FLECHE_DROITE,
  TRACE_FLECHE_GAUCHE,
} from '../ui/AssistantEtapes';
import {
  LONGUEUR_NIR_AFFICHEE,
  chiffresNir,
  formaterNir,
  formaterTelephone,
  longueurTelephoneAffichee,
  nirIncomplet,
  telephoneIncomplet,
} from '../../lib/saisie-numeros';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import { useCreerApprenant, type EntreeInscription, type ModeDossier } from '../../lib/useApprenants';
import { useReferentiels, type CommunePostale } from '../../lib/useDocuments';
import { useSites } from '../../lib/useSites';
import { ErreurApi } from '../../lib/api';

/**
 * Saisie d'une nouvelle inscription par le pôle administratif (CDC §4.1).
 *
 * Le formulaire couvre le bloc « L'apprenti(e) » du CERFA FA13 en entier :
 * c'est là que ces données sont connues, et les ressaisir plus tard à partir
 * d'un dossier papier est exactement ce que la plateforme doit supprimer
 * (saisie unique). Une trentaine de rubriques d'affilée décourageant la
 * saisie, elles sont réparties en étapes, chacune tenant dans un écran de
 * tablette sans défilement interminable.
 *
 * Seuls le nom, le prénom et l'antenne restent exigés : une inscription se
 * prend souvent au téléphone, et bloquer sur le NIR ou le code de dernière
 * classe pousserait à inventer des valeurs — qui partiraient ensuite sur un
 * document contractuel. Le reste se complète dans le dossier.
 *
 * Le bloc CERFA est **masqué** en mode OF, pas affiché vide : un organisme de
 * formation ne remplit pas de contrat d'apprentissage. Les étapes se
 * recomposent donc quand le mode change.
 */
export function FormulaireInscription({
  ouvert,
  onFermer,
  siteParDefaut,
  onCree,
}: {
  ouvert: boolean;
  onFermer: () => void;
  /** Antenne présélectionnée : celle du périmètre courant, si une seule. */
  siteParDefaut: string;
  onCree: (id: string) => void;
}): JSX.Element {
  const creation = useCreerApprenant();
  const { data: sites } = useSites();
  const { data: referentiels } = useReferentiels();

  const [entree, setEntree] = useState<EntreeInscription>(() => vierge(siteParDefaut));
  const [etape, setEtape] = useState(0);
  const [manques, setManques] = useState<string[]>([]);
  const etaitOuvert = useRef(false);

  // `useState` fige l'état initial au premier rendu, et ce composant reste
  // monté tant que l'écran est affiché : sans cet effet, la fenêtre s'ouvre sur
  // l'antenne connue au chargement de la page — c'est-à-dire aucune, le
  // périmètre et la liste des sites n'arrivant qu'après. Le formulaire repart
  // donc à neuf à chaque ouverture, et adopte l'antenne dès qu'elle est connue,
  // sans jamais écraser un choix déjà fait à la main.
  useEffect(() => {
    if (!ouvert) {
      etaitOuvert.current = false;
      return;
    }
    const ouverture = !etaitOuvert.current;
    etaitOuvert.current = true;
    if (ouverture) {
      setEtape(0);
      setManques([]);
    }
    setEntree((precedent) => {
      if (ouverture) return vierge(siteParDefaut);
      if (!siteParDefaut || precedent.siteCode !== '') return precedent;
      return { ...precedent, siteCode: siteParDefaut };
    });
  }, [ouvert, siteParDefaut]);

  const modifier = <Cle extends keyof EntreeInscription>(
    cle: Cle,
    valeur: EntreeInscription[Cle],
  ): void => setEntree((precedent) => ({ ...precedent, [cle]: valeur }));

  const nomenclatures = referentiels?.cerfaApprenti;
  const sitesDisponibles = sites?.sites ?? [];
  const formations = referentiels?.formations ?? [];
  const communes = referentiels?.communesReunion ?? [];
  const cerfa = entree.mode === 'CFA';
  const mineur = minoriteALEntree(entree.dateNaissance, entree.dateEntree);

  // Le représentant légal ne concerne que l'apprenti(e) mineur(e) non
  // émancipé(e) : l'étape n'apparaît qu'une fois la minorité établie par la
  // date de naissance. La montrer à tous ferait remplir un bloc sans objet, et
  // ces coordonnées partiraient telles quelles sur le CERFA.
  const etapes = useMemo(
    () =>
      [
        'Identité',
        'Adresse',
        'Situation',
        ...(cerfa && mineur ? ['Représentant légal'] : []),
      ] as string[],
    [cerfa, mineur],
  );

  // Passer en OF depuis la dernière étape laisserait l'index hors des étapes
  // restantes, donc un corps de fenêtre vide.
  const etapeCourante = Math.min(etape, etapes.length - 1);
  const derniere = etapeCourante === etapes.length - 1;

  // Rubriques refusées par l'API, telles qu'elle les nomme.
  const refus = detailsRefus(creation.error);

  const fermer = (): void => {
    creation.reset();
    setEntree(vierge(siteParDefaut));
    setEtape(0);
    setManques([]);
    onFermer();
  };

  /** Rubriques obligatoires non renseignées, dans l'ordre du formulaire. */
  const manquants = (): string[] => {
    const absents: string[] = [];
    if (entree.nom.trim() === '') absents.push('le nom de naissance');
    if (entree.prenom.trim() === '') absents.push('le premier prénom');
    if (entree.siteCode === '') absents.push("l'antenne de rattachement");
    return absents;
  };

  /**
   * Saisies commencées mais inachevées, avec l'étape où les reprendre.
   *
   * L'API refuse un NIR de treize caractères — sa clé ne peut pas se vérifier —
   * et le formulaire le laissait partir : le dossier était perdu au retour, sur
   * un message générique. Mieux vaut le dire ici, à côté du champ concerné.
   */
  const inacheves = (): { texte: string; etape: number }[] => {
    const soucis: { texte: string; etape: number }[] = [];
    if (cerfa && nirIncomplet(entree.nir)) {
      soucis.push({
        texte: `le NIR est incomplet (${chiffresNir(entree.nir).length} caractères sur 15)`,
        etape: etapes.indexOf('Identité'),
      });
    }
    if (telephoneIncomplet(entree.telephone)) {
      soucis.push({
        texte: 'le téléphone est incomplet (10 chiffres attendus)',
        etape: etapes.indexOf('Adresse'),
      });
    }
    return soucis;
  };

  /**
   * Passage à l'étape suivante. Les rubriques obligatoires vivent toutes à la
   * première étape : on ne la quitte pas sans elles, plutôt que de laisser
   * remplir trois écrans avant d'annoncer qu'il manque le nom.
   */
  const avancer = (): void => {
    if (etapeCourante === 0) {
      const absents = manquants();
      if (absents.length > 0) {
        setManques(absents);
        return;
      }
    }
    setManques([]);
    setEtape(etapeCourante + 1);
  };

  const envoyer = (evenement: React.FormEvent): void => {
    evenement.preventDefault();

    // Les champs obligatoires vivent à la première étape, qui n'est plus montée
    // ici : la validation native du navigateur ne les voit pas. On les
    // revérifie donc à la main, et on ramène à l'étape où les corriger.
    const absents = manquants();
    if (absents.length > 0) {
      setManques(absents);
      setEtape(0);
      return;
    }

    const commences = inacheves();
    if (commences.length > 0) {
      setManques(commences.map((souci) => souci.texte));
      setEtape(commences[0]!.etape);
      return;
    }
    setManques([]);

    // Les champs optionnels vides ne sont pas transmis : l'API refuserait une
    // chaîne vide là où elle attend un e-mail ou une date. Le représentant légal
    // est écarté dès que l'étape ne s'applique plus : une date de naissance
    // corrigée en cours de saisie laisserait sinon partir les coordonnées d'un
    // représentant sur le dossier d'un(e) majeur(e).
    const representantAttendu = cerfa && mineur;
    const charge = Object.fromEntries(
      Object.entries(entree).filter(
        ([cle, valeur]) =>
          valeur !== '' &&
          valeur !== undefined &&
          (representantAttendu || !cle.startsWith('representant')),
      ),
    ) as unknown as EntreeInscription;

    creation.mutate(charge, {
      onSuccess: (resultat) => {
        setEntree(vierge(siteParDefaut));
        setEtape(0);
        creation.reset();
        onCree(resultat.id);
      },
    });
  };

  return (
    <Modale
      titre="Nouvelle inscription"
      sousTitre="Le dossier est créé au statut « inscrit ». Les pièces attendues sont calculées à partir de la situation renseignée."
      ouverte={ouvert}
      onFermer={fermer}
      largeur="max-w-3xl"
    >
      <Progression etapes={etapes} courante={etapeCourante} onAller={setEtape} />

      <form onSubmit={envoyer} className="space-y-5 px-4 py-5 sm:px-6">
        {creation.error && (
          <div className="space-y-2">
            <MessageErreur
              message={
                creation.error instanceof ErreurApi
                  ? creation.error.message
                  : "L'inscription n'a pas pu être enregistrée."
              }
            />
            {/* L'API dit quelle rubrique elle refuse et pourquoi. Ne montrer
                que « données mal formées » obligerait à repasser les quatre
                étapes à l'aveugle pour trouver le champ en cause. */}
            {refus.length > 0 && (
              <ul className="space-y-1 rounded-xl bg-red-50 px-3 py-2 text-[11px] text-red-700">
                {refus.map((detail) => (
                  <li key={`${detail.champ}-${detail.message}`}>
                    <span className="font-semibold">{libelleChamp(detail.champ)}</span> :{' '}
                    {detail.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {manques.length > 0 && (
          <MessageErreur message={`À reprendre avant de créer le dossier : ${manques.join(', ')}.`} />
        )}

        {etapes[etapeCourante] === 'Identité' && (
          <EtapeIdentite
            entree={entree}
            modifier={modifier}
            cerfa={cerfa}
            sites={sitesDisponibles}
            regimesSociaux={nomenclatures?.regimesSociaux ?? []}
          />
        )}

        {etapes[etapeCourante] === 'Adresse' && (
          <EtapeAdresse entree={entree} modifier={modifier} communes={communes} />
        )}

        {etapes[etapeCourante] === 'Situation' && (
          <EtapeSituation
            entree={entree}
            modifier={modifier}
            cerfa={cerfa}
            formations={formations}
            situations={nomenclatures?.situationsAvantContrat ?? []}
            classes={nomenclatures?.dernieresClasses ?? []}
            diplomes={nomenclatures?.diplomes ?? []}
          />
        )}

        {etapes[etapeCourante] === 'Représentant légal' && (
          <EtapeRepresentant entree={entree} modifier={modifier} communes={communes} />
        )}

        {/* Barre d'actions collante : sur tablette, le pouce reste en bas de
            l'écran et le formulaire d'une étape peut dépasser la hauteur. */}
        <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 pb-1 pt-4 sm:-mx-6 sm:px-6">
          <BoutonSecondaire onClick={fermer}>Annuler</BoutonSecondaire>
          <div className="flex items-center gap-2">
            {etapeCourante > 0 && (
              <BoutonSecondaire onClick={() => setEtape(etapeCourante - 1)}>
                <span className="flex items-center gap-1.5">
                  <Icone trace={TRACE_FLECHE_GAUCHE} classe="h-4 w-4" />
                  Précédent
                </span>
              </BoutonSecondaire>
            )}
            {derniere ? (
              <BoutonPrincipal type="submit" disabled={creation.isPending}>
                <span className="flex items-center gap-1.5">
                  <Icone trace={TRACE_COCHE} classe="h-4 w-4" />
                  {creation.isPending ? 'Enregistrement…' : 'Créer le dossier'}
                </span>
              </BoutonPrincipal>
            ) : (
              <BoutonPrincipal type="button" onClick={avancer}>
                <span className="flex items-center gap-1.5">
                  Suivant
                  <Icone trace={TRACE_FLECHE_DROITE} classe="h-4 w-4" />
                </span>
              </BoutonPrincipal>
            )}
          </div>
        </div>
      </form>
    </Modale>
  );
}

// ---------------------------------------------------------------------------
// Étapes
// ---------------------------------------------------------------------------

type Modificateur = <Cle extends keyof EntreeInscription>(
  cle: Cle,
  valeur: EntreeInscription[Cle],
) => void;

interface EntreeNomenclature {
  code: string;
  libelle: string;
  sansDiplome?: boolean;
}

function EtapeIdentite({
  entree,
  modifier,
  cerfa,
  sites,
  regimesSociaux,
}: {
  entree: EntreeInscription;
  modifier: Modificateur;
  cerfa: boolean;
  sites: { code: string; nom: string }[];
  regimesSociaux: EntreeNomenclature[];
}): JSX.Element {
  return (
    <>
      <Section titre="Dossier">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Antenne de rattachement">
            <select
              value={entree.siteCode}
              onChange={(evenement) => modifier('siteCode', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">À choisir</option>
              {sites.map((site) => (
                <option key={site.code} value={site.code}>
                  {site.nom} ({site.code})
                </option>
              ))}
            </select>
          </Champ>
          <Champ
            libelle="Mode de dossier"
            aide="CFA : contrat d’apprentissage et CERFA. OF : session de formation et certificat."
          >
            <select
              value={entree.mode}
              onChange={(evenement) => modifier('mode', evenement.target.value as ModeDossier)}
              className={CLASSE_CHAMP}
            >
              <option value="CFA">CFA · apprentissage</option>
              <option value="OF">OF · organisme de formation</option>
            </select>
          </Champ>
        </div>
      </Section>

      <Section titre="Identité">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ
            libelle="Nom de naissance"
            obligatoire
            aide={cerfa ? 'Tel qu’il figure à l’état civil.' : undefined}
          >
            <input
              required
              maxLength={80}
              autoComplete="family-name"
              value={entree.nom}
              onChange={(evenement) => modifier('nom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {cerfa && (
            <Champ libelle="Nom d’usage" aide="À ne remplir que s’il diffère du nom de naissance.">
              <input
                maxLength={80}
                value={entree.nomUsage ?? ''}
                onChange={(evenement) => modifier('nomUsage', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          )}
          <Champ
            libelle={cerfa ? 'Premier prénom selon l’état civil' : 'Prénom'}
            obligatoire
          >
            <input
              required
              maxLength={80}
              autoComplete="given-name"
              value={entree.prenom}
              onChange={(evenement) => modifier('prenom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {cerfa && (
            <Champ
              libelle="NIR (numéro de sécurité sociale)"
              aide="13 chiffres puis la clé sur 2. La clé est vérifiée à l’enregistrement."
              erreur={
                nirIncomplet(entree.nir)
                  ? `${chiffresNir(entree.nir).length} caractères sur 15`
                  : undefined
              }
            >
              <input
                inputMode="numeric"
                autoComplete="off"
                maxLength={LONGUEUR_NIR_AFFICHEE}
                placeholder="2 09 05 97 412 345 26"
                value={entree.nir ?? ''}
                onChange={(evenement) => modifier('nir', formaterNir(evenement.target.value))}
                aria-invalid={nirIncomplet(entree.nir) || undefined}
                className={`${nirIncomplet(entree.nir) ? CLASSE_CHAMP_FAUTIF : CLASSE_CHAMP} font-mono tracking-wide`}
              />
            </Champ>
          )}
          <Champ
            libelle="Date de naissance"
            aide="Détermine la minorité à l’entrée, et donc les pièces du représentant légal."
            obligatoire
          >
            <input
              required
              type="date"
              max={aujourdhui()}
              value={entree.dateNaissance ?? ''}
              onChange={(evenement) => modifier('dateNaissance', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Sexe">
            <select
              value={entree.sexe ?? ''}
              onChange={(evenement) =>
                modifier('sexe', (evenement.target.value || undefined) as 'M' | 'F' | undefined)
              }
              className={CLASSE_CHAMP}
            >
              <option value="">Non renseigné</option>
              <option value="M">Masculin</option>
              <option value="F">Féminin</option>
            </select>
          </Champ>
          {cerfa && (
            <Champ libelle="Département de naissance" aide="Code à deux ou trois chiffres : 974, 2A…">
              <input
                maxLength={3}
                placeholder="974"
                value={entree.departementNaissance ?? ''}
                onChange={(evenement) => modifier('departementNaissance', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          )}
          <Champ libelle={cerfa ? 'Commune de naissance' : 'Lieu de naissance'}>
            <input
              maxLength={120}
              value={entree.lieuNaissance ?? ''}
              onChange={(evenement) => modifier('lieuNaissance', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Nationalité">
            <input
              maxLength={60}
              value={entree.nationalite ?? ''}
              onChange={(evenement) => modifier('nationalite', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          {cerfa && (
            <Champ libelle="Régime social">
              <select
                value={entree.regimeSocial ?? ''}
                onChange={(evenement) => modifier('regimeSocial', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {regimesSociaux.map((regime) => (
                  <option key={regime.code} value={regime.code}>
                    {regime.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          )}
        </div>
      </Section>
    </>
  );
}

function EtapeAdresse({
  entree,
  modifier,
  communes,
}: {
  entree: EntreeInscription;
  modifier: Modificateur;
  communes: CommunePostale[];
}): JSX.Element {
  return (
    <>
      <Section
        titre="Adresse"
        aide="Le numéro et la voie sont séparés comme sur le CERFA ; la ligne d’adresse affichée ailleurs est recomposée automatiquement."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
          <div className="sm:col-span-1 min-w-0">
            <Champ libelle="N°">
              <input
                maxLength={10}
                value={entree.numeroVoie ?? ''}
                onChange={(evenement) => modifier('numeroVoie', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
          <div className="sm:col-span-5 min-w-0">
            <Champ libelle="Voie">
              <input
                maxLength={160}
                autoComplete="address-line1"
                value={entree.voie ?? ''}
                onChange={(evenement) => modifier('voie', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
          <div className="sm:col-span-6 min-w-0">
            <Champ libelle="Complément" aide="Bâtiment, résidence, lieu-dit.">
              <input
                maxLength={160}
                autoComplete="address-line2"
                value={entree.complementAdresse ?? ''}
                onChange={(evenement) => modifier('complementAdresse', evenement.target.value)}
                className={CLASSE_CHAMP}
              />
            </Champ>
          </div>
          <CodePostalEtCommune
            identifiant="adresse-apprenant"
            communes={communes}
            codePostal={entree.codePostal ?? ''}
            commune={entree.ville ?? ''}
            onChange={(codePostal, commune) => {
              modifier('codePostal', codePostal);
              modifier('ville', commune);
            }}
          />
        </div>
      </Section>

      <Section titre="Contact">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ
            libelle="Téléphone"
            aide="10 chiffres, ou format international (+262…)."
            erreur={
              telephoneIncomplet(entree.telephone)
                ? 'Numéro incomplet : 10 chiffres attendus.'
                : undefined
            }
            obligatoire
          >
            <input
              required
              type="tel"
              inputMode="tel"
              maxLength={longueurTelephoneAffichee(entree.telephone)}
              autoComplete="tel"
              placeholder="06 92 55 12 09"
              value={entree.telephone ?? ''}
              onChange={(evenement) =>
                modifier('telephone', formaterTelephone(evenement.target.value))
              }
              aria-invalid={telephoneIncomplet(entree.telephone) || undefined}
              className={telephoneIncomplet(entree.telephone) ? CLASSE_CHAMP_FAUTIF : CLASSE_CHAMP}
            />
          </Champ>
          <Champ
            libelle="Courriel"
            aide="Sert à l’envoi du lien de création de l’espace candidat."
          >
            <input
              type="email"
              autoComplete="email"
              value={entree.email ?? ''}
              onChange={(evenement) => modifier('email', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </Section>
    </>
  );
}

function EtapeSituation({
  entree,
  modifier,
  cerfa,
  formations,
  situations,
  classes,
  diplomes,
}: {
  entree: EntreeInscription;
  modifier: Modificateur;
  cerfa: boolean;
  formations: { code: string; titre: string }[];
  situations: EntreeNomenclature[];
  classes: EntreeNomenclature[];
  diplomes: EntreeNomenclature[];
}): JSX.Element {
  // « Aucun diplôme » au CERFA et « Titulaire d'un diplôme » ne cohabitent pas :
  // la case suit le choix, pour que la copie d'un diplôme inexistant ne soit ni
  // attendue ni relancée. Elle se recoche si l'on revient sur ce choix.
  const sansDiplome = (code: string | undefined): boolean =>
    diplomes.some((diplome) => diplome.code === code && diplome.sansDiplome === true);
  const choisirDiplomeLePlusEleve = (code: string): void => {
    modifier('diplomeLePlusEleve', code);
    if (sansDiplome(code)) modifier('titulaireDiplome', false);
    else if (sansDiplome(entree.diplomeLePlusEleve)) modifier('titulaireDiplome', true);
  };

  return (
    <>
      <Section titre="Parcours visé">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Formation visée">
            <select
              value={entree.formationCode ?? ''}
              onChange={(evenement) => modifier('formationCode', evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">À définir</option>
              {formations.map((formation) => (
                <option key={formation.code} value={formation.code}>
                  {formation.titre} ({formation.code})
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Date d’entrée prévue">
            <input
              type="date"
              value={entree.dateEntree ?? ''}
              onChange={(evenement) => modifier('dateEntree', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </Section>

      {cerfa && (
        <Section
          titre="Situation avant ce contrat"
          aide="Codes du CERFA : le formulaire officiel attend le code, pas le libellé."
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Champ libelle="Situation avant ce contrat">
              <select
                value={entree.situationAvantContrat ?? ''}
                onChange={(evenement) => modifier('situationAvantContrat', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseignée</option>
                {situations.map((situation) => (
                  <option key={situation.code} value={situation.code}>
                    {situation.code} · {situation.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Dernier diplôme ou titre préparé">
              <select
                value={entree.dernierDiplomePrepare ?? ''}
                onChange={(evenement) => modifier('dernierDiplomePrepare', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {diplomes.map((diplome) => (
                  <option key={diplome.code} value={diplome.code}>
                    {diplome.code} · {diplome.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Dernière classe ou année suivie">
              <select
                value={entree.derniereClasseSuivie ?? ''}
                onChange={(evenement) => modifier('derniereClasseSuivie', evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseignée</option>
                {classes.map((classe) => (
                  <option key={classe.code} value={classe.code}>
                    {classe.code} · {classe.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ libelle="Diplôme ou titre le plus élevé obtenu">
              <select
                value={entree.diplomeLePlusEleve ?? ''}
                onChange={(evenement) => choisirDiplomeLePlusEleve(evenement.target.value)}
                className={CLASSE_CHAMP}
              >
                <option value="">Non renseigné</option>
                {diplomes.map((diplome) => (
                  <option key={diplome.code} value={diplome.code}>
                    {diplome.code} · {diplome.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <div className="sm:col-span-2 min-w-0">
              <Champ
                libelle="Intitulé précis du dernier diplôme ou titre préparé"
                aide="En toutes lettres, tel qu’il sera reporté sur le CERFA."
              >
                <input
                  maxLength={200}
                  value={entree.intitulePrecisDernierDiplome ?? ''}
                  onChange={(evenement) =>
                    modifier('intitulePrecisDernierDiplome', evenement.target.value)
                  }
                  className={CLASSE_CHAMP}
                />
              </Champ>
            </div>
          </div>
        </Section>
      )}

      {cerfa && (
        <Section titre="Déclarations de l’apprenti(e)">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Case
              libelle="Sportif(ve) de haut niveau"
              aide="Inscrit(e) sur la liste officielle"
              coche={entree.sportifHautNiveau}
              onBasculer={() => modifier('sportifHautNiveau', !entree.sportifHautNiveau)}
            />
            <Case
              libelle="Projet de création ou reprise d’entreprise"
              aide="Déclaré au moment du contrat"
              coche={entree.projetCreationEntreprise}
              onBasculer={() =>
                modifier('projetCreationEntreprise', !entree.projetCreationEntreprise)
              }
            />
          </div>
        </Section>
      )}

      <Section
        titre="Situation administrative"
        aide="Chaque case cochée ajoute une pièce justificative à la liste attendue."
      >
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Case
            libelle="Hébergé(e) par un tiers"
            aide="Attestation d’hébergement"
            coche={entree.hebergeParTiers}
            onBasculer={() => modifier('hebergeParTiers', !entree.hebergeParTiers)}
          />
          <Case
            libelle="RQTH"
            aide="Attestation de reconnaissance"
            coche={entree.rqth}
            onBasculer={() => modifier('rqth', !entree.rqth)}
          />
          <Case
            libelle="Déjà apprenti(e)"
            aide="Contrat d’apprentissage précédent"
            coche={entree.dejaApprenti}
            onBasculer={() => modifier('dejaApprenti', !entree.dejaApprenti)}
          />
          <Case
            libelle="Inscrit(e) à France Travail"
            aide="Attestation d’inscription"
            coche={entree.demandeurEmploi}
            onBasculer={() => modifier('demandeurEmploi', !entree.demandeurEmploi)}
          />
          <Case
            libelle="Titulaire d’un diplôme"
            aide="Copie du ou des diplômes"
            coche={entree.titulaireDiplome}
            onBasculer={() => modifier('titulaireDiplome', !entree.titulaireDiplome)}
          />
        </div>

        {/* Les droits attachés à la RQTH ne se posent qu'à défaut de RQTH :
            affichés en permanence, ils se cocheraient par habitude au-dessus
            d'une RQTH déjà déclarée, ce que le CERFA ne prévoit pas. */}
        {cerfa && !entree.rqth && (
          <div className="mt-3 rounded-lg border border-gray-200 bg-codialis-sand/60 p-3">
            <p className="text-xs font-semibold text-codialis-navy">
              À défaut de RQTH, droits attachés à la RQTH
            </p>
            <p className="mt-0.5 text-[11px] text-codialis-gray">
              Page 4 de la notice du CERFA.
            </p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Case
                libelle="Équivalence jeunes"
                aide="Droits ouverts sans RQTH"
                coche={entree.rqthEquivalenceJeunes}
                onBasculer={() =>
                  modifier('rqthEquivalenceJeunes', !entree.rqthEquivalenceJeunes)
                }
              />
              <Case
                libelle="Extension BOE"
                aide="Bénéficiaire de l’obligation d’emploi"
                coche={entree.rqthExtensionBoe}
                onBasculer={() => modifier('rqthExtensionBoe', !entree.rqthExtensionBoe)}
              />
            </div>
          </div>
        )}
      </Section>
    </>
  );
}

function EtapeRepresentant({
  entree,
  modifier,
  communes,
}: {
  entree: EntreeInscription;
  modifier: Modificateur;
  communes: CommunePostale[];
}): JSX.Element {
  return (
    <Section
      titre="Représentant légal"
      aide="À renseigner si l’apprenti(e) est mineur(e) non émancipé(e)."
    >
      <p className="mb-3 flex items-start gap-2 rounded-lg border border-codialis-mint/60 bg-codialis-mint/10 p-3 text-xs text-codialis-navy">
        <Icone trace={TRACE_ALERTE} classe="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          La date de naissance saisie donne un(e) apprenti(e) mineur(e) à l’entrée en
          formation : le représentant légal et ses pièces sont attendus.
        </span>
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ libelle="Nom de naissance">
          <input
            maxLength={80}
            value={entree.representantNom ?? ''}
            onChange={(evenement) => modifier('representantNom', evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <Champ libelle="Prénom">
          <input
            maxLength={80}
            value={entree.representantPrenom ?? ''}
            onChange={(evenement) => modifier('representantPrenom', evenement.target.value)}
            className={CLASSE_CHAMP}
          />
        </Champ>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-6">
        <div className="sm:col-span-1 min-w-0">
          <Champ libelle="N°">
            <input
              maxLength={10}
              value={entree.representantNumeroVoie ?? ''}
              onChange={(evenement) =>
                modifier('representantNumeroVoie', evenement.target.value)
              }
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <div className="sm:col-span-5 min-w-0">
          <Champ libelle="Voie">
            <input
              maxLength={160}
              value={entree.representantVoie ?? ''}
              onChange={(evenement) => modifier('representantVoie', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <div className="sm:col-span-6 min-w-0">
          <Champ libelle="Complément">
            <input
              maxLength={160}
              value={entree.representantComplement ?? ''}
              onChange={(evenement) =>
                modifier('representantComplement', evenement.target.value)
              }
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        <CodePostalEtCommune
          identifiant="adresse-representant"
          communes={communes}
          codePostal={entree.representantCodePostal ?? ''}
          commune={entree.representantCommune ?? ''}
          onChange={(codePostal, commune) => {
            modifier('representantCodePostal', codePostal);
            modifier('representantCommune', commune);
          }}
        />
        <div className="sm:col-span-6 min-w-0">
          <Champ libelle="Courriel">
            <input
              type="email"
              value={entree.representantCourriel ?? ''}
              onChange={(evenement) => modifier('representantCourriel', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
      </div>
    </Section>
  );
}

function vierge(siteParDefaut: string): EntreeInscription {
  return {
    nom: '',
    prenom: '',
    siteCode: siteParDefaut,
    mode: 'CFA',
    hebergeParTiers: false,
    rqth: false,
    dejaApprenti: false,
    demandeurEmploi: false,
    // Coché d'office : c'est l'absence de diplôme qui se déclare.
    titulaireDiplome: true,
    sportifHautNiveau: false,
    rqthEquivalenceJeunes: false,
    rqthExtensionBoe: false,
    projetCreationEntreprise: false,
  };
}
